import { useAcceptLlmSuggestionMutation, useRejectLlmSuggestionMutation } from '@queries/llm.query';
import { captureSuggestionResolutionFailure } from '@utils/sentry.util';
import { useCallback, useRef } from 'react';

export type SuggestionResolution = 'accept' | 'reject';

/**
 * Shared accept/reject telemetry core for LLM suggestion artifacts: each logId
 * resolves at most once (deduplicated), the matching mutation fires, and
 * failures are reported to Sentry without ever blocking the user. Consumers
 * (`useSuggestionResolutionLog`, `useFullAssessmentResolutionLog`) layer their
 * own supersession tracking on top of `resolveLog`.
 * See docs/iterations/AZ-323/AZ-323.DECISIONS.md (D12).
 */
export const useLlmSuggestionResolver = () => {
  const acceptSuggestionMutation = useAcceptLlmSuggestionMutation();
  const rejectSuggestionMutation = useRejectLlmSuggestionMutation();

  const resolvedLogIdsRef = useRef<Set<string>>(new Set());

  const resolveLog = useCallback(
    (logId: string | undefined, resolution: SuggestionResolution) => {
      if (!logId || resolvedLogIdsRef.current.has(logId)) {
        return;
      }

      resolvedLogIdsRef.current.add(logId);

      const mutation = resolution === 'accept' ? acceptSuggestionMutation : rejectSuggestionMutation;
      mutation.mutateAsync(logId).catch((error: unknown) => {
        captureSuggestionResolutionFailure({ error, logId, resolution });
      });
    },
    [acceptSuggestionMutation, rejectSuggestionMutation],
  );

  const resetResolved = useCallback(() => {
    resolvedLogIdsRef.current = new Set();
  }, []);

  return { resetResolved, resolveLog };
};
